import type { Program, StudentProgramSkill } from '../types';

function sortByName(a: Program, b: Program): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

/** True if parent-pointer graph contains a cycle. */
function hasProgramCycle(programs: Program[]): boolean {
  const byId = new Map(programs.map((p) => [p.id, p]));
  for (const start of programs) {
    const seen = new Set<string>();
    let cur: string | null = start.id;
    let steps = 0;
    while (cur && steps++ <= programs.length + 2) {
      if (seen.has(cur)) return true;
      seen.add(cur);
      const node = byId.get(cur);
      if (!node?.previousProgramId) break;
      cur = node.previousProgramId;
    }
  }
  return false;
}

/**
 * Returns true if setting programId.previousProgramId = newPreviousId would create a cycle.
 * For a program id not yet in the list (create), always returns false.
 * Uses forward edges (parent → children), omitting programId’s current link, to see if
 * newPreviousId lies in programId’s descendant subtree.
 */
export function wouldSetProgramPreviousCreateCycle(
  programs: Program[],
  programId: string,
  newPreviousId: string | null | undefined
): boolean {
  if (!newPreviousId) return false;
  if (newPreviousId === programId) return true;

  const exists = programs.some((p) => p.id === programId);
  if (!exists) return false;

  const childrenByParent = new Map<string, string[]>();
  for (const p of programs) {
    if (p.id === programId) continue;
    if (!p.previousProgramId) continue;
    const arr = childrenByParent.get(p.previousProgramId) ?? [];
    arr.push(p.id);
    childrenByParent.set(p.previousProgramId, arr);
  }

  const stack: string[] = [programId];
  const visited = new Set<string>();
  let steps = 0;
  while (stack.length && steps++ <= programs.length + 2) {
    const n = stack.pop()!;
    if (n === newPreviousId) return true;
    if (visited.has(n)) continue;
    visited.add(n);
    const children = childrenByParent.get(n) ?? [];
    for (const c of children) {
      stack.push(c);
    }
  }
  return false;
}

/** False when program parent links form a cycle (invalid forest). */
export function isProgramForestValid(programs: Program[]): boolean {
  if (programs.length === 0) return true;
  return !hasProgramCycle(programs);
}

function buildChildrenMap(programs: Program[]): Map<string, Program[]> {
  const ids = new Set(programs.map((p) => p.id));
  const childrenByParent = new Map<string, Program[]>();
  for (const p of programs) {
    if (!p.previousProgramId || !ids.has(p.previousProgramId)) continue;
    const list = childrenByParent.get(p.previousProgramId) ?? [];
    list.push(p);
    childrenByParent.set(p.previousProgramId, list);
  }
  for (const [, list] of childrenByParent) {
    list.sort(sortByName);
  }
  return childrenByParent;
}

function getRoots(programs: Program[], ids: Set<string>): Program[] {
  return programs
    .filter((p) => !p.previousProgramId || !ids.has(p.previousProgramId))
    .sort(sortByName);
}

function preorderSubtree(
  root: Program,
  childrenByParent: Map<string, Program[]>
): Program[] {
  const out: Program[] = [root];
  const children = childrenByParent.get(root.id) ?? [];
  for (const c of children) {
    out.push(...preorderSubtree(c, childrenByParent));
  }
  return out;
}

/**
 * Root programs (no predecessor, or predecessor missing from the list), sorted by name.
 */
export function getProgramRoots(programs: Program[]): Program[] {
  if (programs.length === 0) return [];
  if (hasProgramCycle(programs)) return [];
  const ids = new Set(programs.map((p) => p.id));
  return getRoots(programs, ids);
}

/**
 * Children of each program id, sorted by name. Only includes edges to programs in the list.
 */
export function getProgramChildrenByParent(programs: Program[]): Map<string, Program[]> {
  if (programs.length === 0 || hasProgramCycle(programs)) {
    return new Map();
  }
  return buildChildrenMap(programs);
}

/**
 * One array per root tree: depth-first preorder (children sorted by name at each level).
 * If the graph has a cycle, returns a single row of all programs sorted by name.
 */
export function getProgramChains(programs: Program[]): Program[][] {
  if (programs.length === 0) return [];
  if (programs.length === 1) return [[programs[0]]];
  if (hasProgramCycle(programs)) {
    return [[...programs].sort(sortByName)];
  }

  const ids = new Set(programs.map((p) => p.id));
  const childrenByParent = buildChildrenMap(programs);
  const roots = getRoots(programs, ids);
  return roots.map((r) => preorderSubtree(r, childrenByParent));
}

/**
 * Order programs for management UI and API: preorder forest walk (roots by name,
 * then each subtree with children sorted by name). Cycles fall back to name sort.
 */
export function sortProgramsByProgression(programs: Program[]): Program[] {
  return getProgramChains(programs).flat();
}

/**
 * Program IDs in the pathway rooted at rootId (root + all descendants), in safe delete order
 * (children before parents).
 */
export function getProgramPathwaySubtreeDeleteOrder(rootId: string, programs: Program[]): string[] {
  const byId = new Map(programs.map((p) => [p.id, p]));
  if (!byId.has(rootId)) return [];

  const childrenByParent = buildChildrenMap(programs);
  const order: string[] = [];

  function postOrder(id: string) {
    const children = childrenByParent.get(id) ?? [];
    for (const child of children) {
      postOrder(child.id);
    }
    order.push(id);
  }

  postOrder(rootId);
  return order;
}

/** True when every program target skill has a completed row (programs with no skills never auto-complete). */
export function areAllProgramTargetSkillsCompleted(
  program: Program,
  skills: StudentProgramSkill[]
): boolean {
  if (!program.targetSkills || program.targetSkills.length === 0) return false;
  const completedNames = new Set(skills.filter((s) => s.completed).map((s) => s.skillName));
  for (const t of program.targetSkills) {
    if (!completedNames.has(t.name)) return false;
  }
  return true;
}
